import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Check, Sprout } from "lucide-react";
import { discountedPrice } from "../hooks/usePromo";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

/**
 * High-conversion launch promo section, sits ABOVE the pricing cards.
 *
 * Tone: artisan / curated, NOT clearance-sale. Frame the discount as a
 * "coup de pouce" for entrepreneurs ready to attack the season.
 *
 * Conversion levers (in order of importance):
 *   1. Loud striked-through prices — the brain anchors instantly
 *   2. Social-urgency counter ("Plus que X places sur 10")
 *   3. Time-bound deadline (J−X)
 *   4. Single, low-friction CTA → opens the contact / demo modal
 */
export default function LaunchPromoSection({ promo, onContact }) {
  if (!promo?.active) return null;
  const slots = promo.slots_remaining;
  const totalSlots = promo.slots_total;
  const pct = promo.discount_pct;

  const ESS_BASE = 39;
  const VIV_BASE = 59;

  const cards = [
    {
      key: "active",
      title: "La Vitrine Active",
      tagline: "Votre vitrine clé en main, vivante et autonome.",
      base: ESS_BASE,
      promo: discountedPrice(ESS_BASE, pct),
      perks: ["Création incluse (jusqu'à 3 pages)", "Contenu dynamique via Google Sheet", "Hébergement & SSL inclus", "Engagement initial de 6 mois"],
      featured: true,
    },
    {
      key: "croissance",
      title: "La Formule Croissance",
      tagline: "Fidélisez votre clientèle et automatisez votre communication.",
      base: VIV_BASE,
      promo: discountedPrice(VIV_BASE, pct),
      perks: ["Dashboard de gestion dédié (au lieu du Google Sheet)", "Fichier clients (RGPD)", "E-mailing & newsletter (300 mails/jour)", "Boîte e-mail pro personnalisée"],
    },
  ];

  const endLabel = (() => {
    if (!promo.end_date_iso) return null;
    return new Date(promo.end_date_iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  })();

  return (
    <section
      id="promo-lancement"
      className="relative py-20 lg:py-28 bg-[#1F3535] text-white overflow-hidden"
      data-testid="launch-promo-section"
    >
      {/* layered ambient glows */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18),transparent_60%)] pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(196,106,74,0.18),transparent_60%)] pointer-events-none"
      />
      {/* fine grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/></svg>\")",
        }}
      />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={stagger}
        className="relative max-w-6xl mx-auto px-6"
      >
        {/* Eyebrow */}
        <motion.div variants={fadeUp} className="text-center mb-6">
          <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold border border-[#D4AF37]/40 rounded-full px-4 py-1.5">
            <Sprout className="w-3.5 h-3.5" strokeWidth={2} />
            Coup de pouce de saison · édition limitée
          </span>
        </motion.div>

        <motion.h2
          variants={fadeUp}
          className="text-center text-3xl sm:text-4xl lg:text-5xl font-medium font-montserrat tracking-tight mb-5 text-white"
        >
          Attaquez la saison{" "}
          <span className="font-playfair italic font-normal text-[#E2A38C]">
            en force.
          </span>
        </motion.h2>

        <motion.p
          variants={fadeUp}
          className="text-center max-w-2xl mx-auto text-white/80 leading-relaxed font-body font-light mb-3"
        >
          Pour aider les entrepreneurs prêts à passer la seconde, j'offre{" "}
          <span className="font-mono-tech text-[#D4AF37] font-semibold">−{pct}%</span> sur
          la première année d'abonnement. Vous gardez exactement le même service —
          conception, hébergement, support — au tarif d'un café par jour.
        </motion.p>

        <motion.p
          variants={fadeUp}
          className="text-center text-[12px] uppercase tracking-[0.2em] text-[#E8D8A9]/85 font-semibold mb-12"
        >
          Réservé aux 10 premières signatures · jusqu'au {endLabel || "31 mai 2026"}
        </motion.p>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {cards.map((c, i) => (
            <motion.div
              key={c.key}
              variants={fadeUp}
              data-testid={`promo-card-${c.key}`}
              className={`relative overflow-hidden rounded-2xl p-7 sm:p-8 backdrop-blur-sm transition-all duration-300 ${
                c.featured
                  ? "bg-gradient-to-br from-[#C46A4A] to-[#A6553A] ring-2 ring-[#D4AF37]/40 shadow-2xl shadow-[#C46A4A]/30"
                  : "bg-white/[0.06] border border-white/15 hover:border-[#D4AF37]/40"
              }`}
            >
              {c.featured && (
                <span className="absolute top-4 right-4 text-white text-[9px] px-3 py-1 rounded-full font-semibold tracking-[0.18em] uppercase shadow-md bg-[#1F3535]/80 ring-1 ring-[#D4AF37]/30">
                  Recommandé
                </span>
              )}
              <span
                className={`absolute -top-px -left-px text-[10px] px-3 py-1 rounded-br-2xl rounded-tl-2xl font-bold tracking-wider uppercase ${
                  c.featured
                    ? "bg-[#1F3535] text-[#D4AF37]"
                    : "bg-[#D4AF37] text-[#1F3535]"
                }`}
              >
                −{pct}% · 1 an
              </span>

              <div className="pt-6">
                <h3 className="text-2xl font-medium font-montserrat tracking-tight text-white mb-1.5">
                  {c.title}
                </h3>
                <p
                  className={`text-sm italic font-playfair mb-6 ${
                    c.featured ? "text-white/90" : "text-[#E8D8A9]"
                  }`}
                >
                  {c.tagline}
                </p>

                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-[15px] font-mono-tech text-white/40 line-through decoration-[#E2A38C]/70 decoration-2">
                    {c.base} €
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-white/55 font-semibold">
                    Prix public
                  </span>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span
                    className="text-5xl sm:text-6xl font-mono-tech font-semibold leading-none text-white"
                    data-testid={`promo-price-${c.key}`}
                  >
                    {c.promo}
                  </span>
                  <span className="text-2xl font-mono-tech text-white/85">€</span>
                  <span className="text-sm text-white/65 font-body">/mois</span>
                </div>
                <p
                  className={`text-xs font-body mb-6 ${
                    c.featured ? "text-white/85" : "text-[#E8D8A9]/85"
                  }`}
                >
                  pendant 12 mois, puis tarif standard.
                </p>

                <ul className="space-y-2 mb-2">
                  {c.perks.map((p, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-2 text-sm font-body text-white/90"
                    >
                      <Check
                        className={`w-4 h-4 mt-0.5 shrink-0 ${
                          c.featured ? "text-[#1F3535]" : "text-[#D4AF37]"
                        }`}
                        strokeWidth={2.5}
                      />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Slots counter + CTA */}
        <motion.div variants={fadeUp} className="max-w-3xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 rounded-2xl bg-white/[0.04] border border-white/15 px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="relative w-12 h-12 shrink-0">
                <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                  <circle
                    cx="18"
                    cy="18"
                    r="15.5"
                    fill="none"
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth="2.5"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="15.5"
                    fill="none"
                    stroke="#D4AF37"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeDasharray={`${(slots / totalSlots) * 97.4} 97.4`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-mono-tech font-semibold text-[#D4AF37]">
                  {slots}
                </span>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E8D8A9] font-semibold mb-1">
                  Disponibilité réelle
                </p>
                <p className="text-base font-medium font-body text-white" data-testid="promo-slots-text">
                  {slots <= 0
                    ? "Les 10 places sont prises — merci !"
                    : slots === 1
                    ? "Plus qu'une seule place sur 10"
                    : `Plus que ${slots} places sur ${totalSlots}`}
                </p>
                <p className="text-xs text-white/60 font-body italic">
                  Premier inscrit, premier servi.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onContact}
              disabled={slots <= 0}
              data-testid="promo-cta-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E0C158] disabled:opacity-40 disabled:cursor-not-allowed text-[#1F3535] font-semibold tracking-wider uppercase text-xs px-7 h-12 rounded-full transition-all shadow-lg shadow-black/30 hover:shadow-[#D4AF37]/30 hover:-translate-y-0.5"
            >
              <Sparkles className="w-4 h-4" strokeWidth={2.2} />
              {slots <= 0 ? "Liste d'attente" : "Réserver ma place"}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <p className="text-center text-[11px] text-white/55 mt-4 font-body italic">
            La remise s'applique automatiquement à la signature, sur la première année.
            Sans frais supplémentaires — vous récupérez le tarif standard ensuite.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}
